import React, {RefObject} from "react";
import Nullable from "@data-force/app/interfaces/nullable";
import {Translate} from "react-localize-redux";
import {connect} from "react-redux";
import {Form} from "antd";
import {
    EditorType,
    PresentationElementType,
    SchemaElementsFactory,
    UIElementType,
    ValidationLevel,
} from "@data-force/app";
import "./JobMetadata.scss";
import {ElementsValuesType} from "@data-force/app/interfaces/store/actions/payload/schema/schemaElements";
import {SchemaState} from "@data-force/app/interfaces/store/reducers/schema";
import StoreState from "../../../interfaces/store/storeState";
import TextElement from "../../DynamicForm/TextElement/TextElement";
import TextAreaElement from "../../DynamicForm/TextAreaElement/TextAreaElement";
import RadioElement from "../../DynamicForm/RadioElement/RadioElement";
import CheckboxElement from "../../DynamicForm/CheckboxElement/CheckboxElement";
import ComboElement from "../../DynamicForm/ComboElement/ComboElement";
import MultiselectElement from "../../DynamicForm/MultiSelectElement/MultiselectElement";
import NumberElement from "../../DynamicForm/NumberElement/NumberElement";
import LiteralElement from "../../DynamicForm/LiteralElement/LiteralElement";
import Container from "../../DynamicForm/Container/Container";
import RepeatableActions from "../../DynamicForm/RepeatableActions/RepeatableActions";
import HistoryParams from "../../../interfaces/historyParams";
import {getHistoryParams, isPhaseExpired, isTaskFinished} from "../../../utils/common";
import {TaskEntity} from "../../../interfaces/store/actions/payload/task";
import {SaveBatchFieldsAction} from "../../../interfaces/store/actions/job";
import {saveBatchFields} from "../../../store/actions/job";
import {JobPhase} from "../../../interfaces/store/actions/payload/job";

interface StateProps {
    editor: Nullable<EditorType>
    schemaKey: string
    jobId: string
    isReadOnlyJob: boolean
}

interface OwnProps {
    headerReference: RefObject<HTMLHeadingElement>
}

interface DispatchProps {
    saveBatchFields(): SaveBatchFieldsAction
}

type Props = StateProps & OwnProps & DispatchProps;

class JobMetadata extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobMetadata.JobMetadata";

    constructor(props: Props) {
        super(props);

        this.onSave = this.onSave.bind(this);
    }

    onSave() {
        this.props.saveBatchFields();
    }

    render() {
        const {translationPath}: typeof JobMetadata = JobMetadata;
        const {
            editor, schemaKey, jobId, isReadOnlyJob, headerReference,
        }: Props = this.props;

        return (
            <div className="df-job-metadata">
                <h2
                    className="df-section-title"
                    ref={headerReference}
                    tabIndex={-1}
                >
                    <Translate id={`${translationPath}.batchMetadata.text`}/>
                </h2>
                <div className="df-metadata-form">
                    {editor && (
                        <Form
                            layout="vertical"
                            className="df-dynamic-form"
                        >
                            <SchemaElementsFactory
                                element={editor}
                                references={editor.batchFieldReferences!}
                                schemaKey={schemaKey}
                                initFallbackValues
                                validationLevel={ValidationLevel.FORM}
                                metadataId={jobId}
                                elementsValuesType={ElementsValuesType.JOB}
                                uiElements={{
                                    [UIElementType.TEXT]: TextElement,
                                    [UIElementType.TEXTAREA]: TextAreaElement,
                                    [UIElementType.RADIO]: RadioElement,
                                    [UIElementType.CHECKBOX]: CheckboxElement,
                                    [UIElementType.COMBO]: ComboElement,
                                    [UIElementType.MULTISELECT]: MultiselectElement,
                                    [UIElementType.LITERAL]: LiteralElement,
                                    [UIElementType.INT]: NumberElement,
                                }}
                                presentationElements={{
                                    [PresentationElementType.CONTAINER]: Container,
                                    [PresentationElementType.REPEATABLE_ACTIONS]: RepeatableActions,
                                }}
                                onSave={this.onSave}
                                metadataReadOnly={isReadOnlyJob}
                            />
                        </Form>
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps({
    schema,
    project,
    job: {tasks, jobPhase},
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const {parsedSchemas}: SchemaState = schema;
    const {accessStatus}: JobPhase = jobPhase;
    const isReadOnlyJob: boolean = tasks.some(
        (task: TaskEntity): boolean => isTaskFinished(task.status) || isPhaseExpired(accessStatus),
    );
    return {
        editor: parsedSchemas[schemaKey] as EditorType,
        schemaKey,
        jobId: historyParams?.jobId,
        isReadOnlyJob,
    };
}

const mapDispatchToProps: DispatchProps = {
    saveBatchFields,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobMetadata);
